import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import "./Header.scss";

import { connect } from "react-redux";
import { Auth, User } from "../../../ts/Auth";
import { RawObrat } from "../../../ts/Obraty";
import ReduxState from "../../../ts/Redux";

import { BsExclamationTriangle } from "react-icons/bs";
import { FaFileImport } from "react-icons/fa";
import { IoMdAdd } from "react-icons/io";
import { readString } from "react-papaparse";
import { kontaktModel } from "../../../api/models/kontaktModel";
import { userModel } from "../../../api/models/userModel";
import axios from "../../../axios/axios";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { useSelector } from "../../../redux/store";
import { Skupina } from "../../../ts/CSSkupiny";
import { Errors } from "../../../ts/Errors";
import { Kontakt } from "../../../ts/Kontakt";
import Button from "../../ui/Button/Button";
import ImportPopup, {
  ImportPopupInterface,
} from "../../ui/ImportPopup/ImportPopup";
import AttributeInput from "../../ui/Input/AttributeInput";
import DropdownInput from "../../ui/Input/DropdownInput";
import { LazyAutocomplete } from "../../ui/LazyAutocomplete/LazyAutocomplete";
import { KontaktLazyInput } from "../CenovePonuky/CenovaPonuka/KontaktLazyInput";
import { Filter, useFilterCriteria, useObraty } from "./Obraty";
import {
  GET_OBRATY_BY_FILTER,
  IS_FILTER_EMPTY,
  MAP_CSV_OBRATY_TO_JSON,
  onlyUnique,
} from "./utils";
import { obratModel } from "../../../api/models/obratModel";
import { ErrorKey, serverErrorOfKey } from "../../../types/server-api/errors";

interface Props {
  auth: Auth;
}

const Header = ({ auth }: Props) => {
  const [errors, setErrors] = useState<Errors>({});
  const [selectedKontakt, setSelectedKontakt] = useState<Kontakt | undefined>(
    undefined
  );
  const [selectedSkupina, setSelectedSkupina] = useState<Skupina | undefined>(
    undefined
  );
  const [selectedMesto, setSelectedMesto] = useState<string | undefined>(
    undefined
  );
  const [selectedOkres, setSelectedOkres] = useState<string | undefined>(
    undefined
  );
  const [selectedObchodnik, setSelectedObchodnik] = useState<User | undefined>(
    undefined
  );
  const [selectedRok, setSelectedRok] = useState<number | undefined>(undefined);
  const [selectedMesiac, setSelectedMesiac] = useState<number | undefined>(
    undefined
  );

  const [filterCriteria, setFilterCriteria] = useFilterCriteria();

  const [cities, setCities] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[]>([]);
  const [rokArray, setRokArray] = useState<number[]>([]);
  const [mesiacArray, setMesiacArray] = useState<number[]>([]);

  const { call } = useQueryWithPopupErrorHandling();

  useEffect(() => {
    call(
      () => kontaktModel.getCitiesAndDistricts(),
      null,
      null,
      ({ cities, districts, rokArray, mesiacArray }) => {
        setCities(cities);
        setDistricts(districts);
        setRokArray(rokArray);
        setMesiacArray(mesiacArray);
      }
    );
  }, []);

  const addObraty = async (obraty: RawObrat[]) => {
    call(
      () => obratModel.createObrat({ obraty }),
      "Pridanie obratov nebolo úspešné",
      "Pridanie obratov bolo úspešné",
      () => {},
      (serverErrors) => {
        if (serverErrorOfKey(ErrorKey.WRONG_FILE_FORMAT, serverErrors)) {
          setErrors({ import: "Nesprávny formát CSV súboru" });
        }
      }
    );
  };

  const importObraty = (e: ChangeEvent<HTMLInputElement>) => {
    let f = e.target.files?.[0];

    if (f) {
      let r = new FileReader();
      r.onload = async (e) => {
        let contents = e.target?.result;
        let csv = readString((contents as string).trim(), {
          header: true,
          complete: async ({ data, errors: parseErrors }) => {
            console.log(parseErrors);
            let mapped =
              parseErrors.length > 0 ? false : MAP_CSV_OBRATY_TO_JSON(data);
            console.log(data, mapped);

            if (mapped) {
              setErrors({});
              const req = await axios.post("/obraty/import-preview", {
                data: mapped,
              });

              importPopupRef.current?.showPopup(
                req.data.array.map((i: any) => ({
                  id_klienta: i.id_klienta,
                  nazov_spolocnosti: i.nazov_spolocnosti,
                  rok: i.rok,
                  mesiac: i.mesiac,
                  suma: i.suma,
                })) as any[],
                (content) => {
                  if (content) {
                    let _content = content.map((c) => {
                      return {
                        id_klienta: parseInt(c.id_klienta),
                        rok: parseInt(c.rok),
                        mesiac: parseInt(c.mesiac),
                        suma: parseFloat(c.suma),
                      };
                    });

                    addObraty(_content);
                  }
                }
              );
            } else {
              setErrors({ ...errors, import: "Nesprávny formát CSV súboru" });
            }
          },
        });
      };
      r.readAsText(f);
    }
  };

  const [obchodnici, setObchodnici] = useState<User[]>([]);

  useEffect(() => {
    let isMounted = true;

    const fetchObchodnici = async () => {
      call(
        () => userModel.getAllUsers(),
        "Získánie obchodníkov nebolo úspešné",
        null,
        (data) => {
          if (isMounted) {
            setObchodnici(data);
          }
        }
      );
    };

    if (auth.user.function === "root") {
      fetchObchodnici();
    }

    return () => {
      isMounted = false;
    };
  }, [auth]);

  const { cs_skupiny } = useSelector((state) => state);

  const CURRENT_FILTER: Filter = {
    kontakt: {
      value: selectedKontakt?._id,
      label: selectedKontakt?.nazev ?? "",
    },
    skupina: {
      value: selectedSkupina?._id,
      label: selectedSkupina?.nazev ?? "",
    },
    obec: { value: selectedMesto, label: selectedMesto ?? "" },
    okres: { value: selectedOkres, label: selectedOkres ?? "" },
    obchodnik: {
      value: selectedObchodnik?._id,
      label: selectedObchodnik
        ? selectedObchodnik.firstName + " " + selectedObchodnik.lastName
        : "",
    },
    rok: { value: selectedRok, label: selectedRok?.toString() ?? "" },
    mesic: { value: selectedMesiac, label: selectedMesiac?.toString() ?? "" },
  };

  const addToFilterCriteria = () => {
    setFilterCriteria([...filterCriteria, CURRENT_FILTER]);
    setSelectedKontakt(undefined);
    setSelectedSkupina(undefined);
    setSelectedMesto(undefined);
    setSelectedOkres(undefined);
    setSelectedObchodnik(undefined);
    setSelectedRok(undefined);
    setSelectedMesiac(undefined);
  };

  const importPopupRef = useRef<ImportPopupInterface>(null);

  return (
    <>
      <ImportPopup ref={importPopupRef} canBeEdited={true} />
      <div className="obraty-header-wrapper">
        <div className="up"></div>
        <div className="down">
          <div className="in-row">
            <div>
              <AttributeInput
                title="Klient / Spoločnosť"
                InputComponent={
                  <KontaktLazyInput
                    value={selectedKontakt ?? null}
                    onChange={(kontakt) =>
                      setSelectedKontakt(kontakt ?? undefined)
                    }
                  />
                }
              />
            </div>
            <div>
              <AttributeInput
                title="Skupina"
                InputComponent={
                  <LazyAutocomplete<Skupina>
                    enableFiltering
                    data={cs_skupiny}
                    value={selectedSkupina ?? null}
                    getOptionLabel={(option) => option.nazev}
                    getOptionKey={(option) => option._id}
                    onChange={(skupina) =>
                      setSelectedSkupina(skupina ?? undefined)
                    }
                  />
                }
              />
            </div>
          </div>
          <div className="in-row">
            <div>
              <AttributeInput
                title="Mesto"
                InputComponent={
                  <LazyAutocomplete<string>
                    enableFiltering
                    data={cities}
                    value={selectedMesto ?? null}
                    getOptionLabel={(option) => option}
                    getOptionKey={(option) => option}
                    onChange={(mesto) => setSelectedMesto(mesto ?? undefined)}
                  />
                }
              />
            </div>
            <div>
              <AttributeInput
                title="Okres"
                InputComponent={
                  <LazyAutocomplete<string>
                    enableFiltering
                    data={districts}
                    value={selectedOkres ?? null}
                    getOptionLabel={(option) => option}
                    getOptionKey={(option) => option}
                    onChange={(okres) => setSelectedOkres(okres ?? undefined)}
                  />
                }
              />
            </div>
            <div>
              <AttributeInput
                title="Obchodník"
                InputComponent={
                  <LazyAutocomplete<User>
                    enableFiltering
                    data={obchodnici}
                    value={selectedObchodnik ?? null}
                    getOptionLabel={(option) =>
                      option.firstName + " " + option.lastName
                    }
                    getOptionKey={(option) => option._id}
                    onChange={(item) => {
                      setSelectedObchodnik(item ?? undefined);
                    }}
                  />
                }
              />
            </div>
          </div>

          <div className="in-row">
            <div className="in-row full-width">
              <div>
                <AttributeInput
                  title="Rok"
                  InputComponent={
                    <LazyAutocomplete<number>
                      enableFiltering
                      data={rokArray}
                      value={selectedRok ?? null}
                      getOptionLabel={(option) => String(option)}
                      getOptionKey={(option) => String(option)}
                      onChange={(rok) => setSelectedRok(rok ?? undefined)}
                    />
                  }
                />
              </div>
              <div>
                <AttributeInput
                  title="Mesiac"
                  InputComponent={
                    <LazyAutocomplete<number>
                      enableFiltering
                      data={mesiacArray}
                      value={selectedMesiac ?? null}
                      getOptionLabel={(option) => String(option)}
                      getOptionKey={(option) => String(option)}
                      onChange={(mesiac) =>
                        setSelectedMesiac(mesiac ?? undefined)
                      }
                    />
                  }
                />
              </div>
            </div>
            <div>
              {auth.user.function === "root" ? (
                <div>
                  <label htmlFor="obraty-import">
                    <div
                      className="styled-button"
                      style={{
                        marginTop: 16,
                        width: 200,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {" "}
                      <FaFileImport
                        size={16}
                        style={{
                          marginRight: 10,
                        }}
                      />{" "}
                      IMPORT OBRATOV
                    </div>
                  </label>
                  {errors.import ? (
                    <div className="error-message import">{errors.import}</div>
                  ) : null}
                  <input
                    type="file"
                    style={{
                      display: "none",
                    }}
                    accept=".csv"
                    onClick={(
                      e: React.MouseEvent<HTMLInputElement, MouseEvent>
                    ) => {
                      const element = e.target as HTMLInputElement;
                      element.value = "";
                    }}
                    onChange={importObraty}
                    id="obraty-import"
                  />
                </div>
              ) : null}
            </div>
            <div className="submit-obrat-wrapper">
              <Button
                style={{
                  marginTop: 16,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                disabled={IS_FILTER_EMPTY(CURRENT_FILTER)}
                onClick={addToFilterCriteria}
              >
                {" "}
                <IoMdAdd
                  size={16}
                  style={{
                    marginRight: 10,
                  }}
                />{" "}
                PRIDAŤ DO VÝBERU
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(Header);
